import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, MapPin } from 'lucide-react';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import type { PodpowiedzMiejsca, StatusPodpowiedzi } from '@/lib/bliskoDo';

/**
 * Wiersz „Szukaj miejsca” i lista wyników miejsc w polu obszaru (plan §1.90). Jeden komponent na
 * pole na pulpicie i nakładkę na telefonie — `dotyk` daje telefonowi wiersze ≥ 48 px i 16 px tekstu.
 */

const wiersz = (dotyk: boolean, aktywny: boolean) =>
  `flex w-full items-center gap-2 text-left outline-none hover:bg-muted focus-visible:bg-muted ${
    dotyk ? 'min-h-12 px-4 py-3 text-base active:bg-muted' : 'px-3 py-2 text-sm'
  } ${aktywny ? 'bg-muted' : ''}`;

export function WierszSzukajMiejsca({
  query,
  dotyk = false,
  aktywny = false,
  id,
  onWybierz,
  onPodglad,
}: {
  query: string;
  dotyk?: boolean;
  aktywny?: boolean;
  id?: string;
  onWybierz: () => void;
  onPodglad?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      id={id}
      data-testid="wiersz-szukaj-miejsca"
      data-aktywny={aktywny || undefined}
      onClick={onWybierz}
      onMouseEnter={onPodglad}
      className={`${wiersz(dotyk, aktywny)} border-t`}
    >
      <MapPin className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{t('search.area.place_row', { query: query.trim() })}</span>
      <span className={`shrink-0 text-muted-foreground ${dotyk ? 'text-xs' : 'text-[11px]'}`}>{t('search.area.place_row_hint')}</span>
    </button>
  );
}

export function ListaMiejscWPolu({
  places,
  status,
  laduje,
  dotyk = false,
  aktywny = -1,
  idPrefix,
  onWroc,
  onWybierz,
  onPodglad,
}: {
  places: PodpowiedzMiejsca[];
  status: StatusPodpowiedzi;
  laduje: boolean;
  dotyk?: boolean;
  /** Pozycja podświetlona strzałkami: 0 = „wróć”, 1… = kolejne wyniki. */
  aktywny?: number;
  /** Prefiks identyfikatorów wierszy dla `aria-activedescendant` pola. */
  idPrefix?: string;
  onWroc: () => void;
  onWybierz: (p: PodpowiedzMiejsca) => void;
  /** Najechanie na wynik: pole przygasza chip miejsca, które ten wynik zastąpi. */
  onPodglad?: (p: PodpowiedzMiejsca | null) => void;
}) {
  const { t } = useTranslation();
  const miasto = t(`search.area.miasto_miejscownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const id = (i: number) => (idPrefix ? `${idPrefix}-${i}` : undefined);
  const tekstStanu = dotyk ? 'px-4 py-4 text-base' : 'px-3 py-2 text-sm';

  return (
    <div data-testid="tryb-miejsc">
      <button
        type="button"
        id={id(0)}
        data-testid="miejsca-wroc"
        data-aktywny={aktywny === 0 || undefined}
        onClick={onWroc}
        className={`${wiersz(dotyk, aktywny === 0)} border-b font-medium text-muted-foreground`}
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t('search.area.place_back')}</span>
      </button>

      {/* Kręciołek tylko, dopóki nie ma żadnego wyniku — tak jak w „Blisko do…”. */}
      {places.length === 0 ? (
        laduje ? (
          <p role="status" data-testid="miejsca-laduje" className={`flex items-center gap-2 text-muted-foreground ${tekstStanu}`}>
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            {t('search.area.place_loading')}
          </p>
        ) : (
          <p role="status" data-testid="miejsca-brak" className={`text-muted-foreground ${tekstStanu}`}>
            {status === 'limited' || status === 'unavailable'
              ? t('search.area.place_unavailable')
              : t('search.filters.blisko.no_results', { miasto })}
          </p>
        )
      ) : (
        <ul data-testid="miejsca-wyniki">
          {places.map((p, i) => (
            <li key={`${p.label}|${p.lat}|${p.lon}`}>
              <button
                type="button"
                id={id(i + 1)}
                data-aktywny={aktywny === i + 1 || undefined}
                onClick={() => onWybierz(p)}
                onMouseEnter={() => onPodglad?.(p)}
                onMouseLeave={() => onPodglad?.(null)}
                className={wiersz(dotyk, aktywny === i + 1)}
              >
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground">{p.label}</span>
                  {p.address && (
                    <span className={`block truncate text-muted-foreground ${dotyk ? 'text-sm' : 'text-xs'}`}>{p.address}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Link „Search by LocationIQ.com” jest warunkiem darmowego planu LocationIQ (PreLaunchChecklist
          4.11) — ten sam napis i link, co pod wynikami „Blisko do…”. */}
      <a
        href="https://locationiq.com"
        target="_blank"
        rel="noopener noreferrer"
        className={`block border-t text-right text-muted-foreground underline-offset-4 hover:underline ${
          dotyk ? 'px-4 py-3 text-xs' : 'px-3 py-1.5 text-[11px]'
        }`}
      >
        {t('search.filters.blisko.attribution')}
      </a>
    </div>
  );
}
